import React, { useState, useEffect } from 'react';
import { Settings, Zap, Database, TestTube, CheckCircle, XCircle, Loader, Users, TrendingUp, RefreshCw, Send, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import WebhookHealth from './WebhookHealth';
import DigestRunHistory from './DigestRunHistory';



const AdminIntegrations = ({ user }) => {
  const [config, setConfig] = useState({
    n8n_webhook_url: '',
    notion_api_key: '',
    notion_database_id: '',
    enabled: true,
    auto_sync_notion: false,
    n8n_events: ['user_signup', 'payment_completed', 'mortgage_updated', 'subscription_changed']
  });
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState({ n8n: false, notion: false });
  const [triggeringEvent, setTriggeringEvent] = useState(null);
  
  const availableEvents = [
    { id: 'user_signup', label: 'User Signup', description: 'When a new user registers' },
    { id: 'payment_completed', label: 'Payment Completed', description: 'When a subscription payment succeeds' },
    { id: 'mortgage_updated', label: 'Mortgage Updated', description: 'When user updates mortgage details' },
    { id: 'subscription_changed', label: 'Subscription Changed', description: 'When subscription tier changes' },
    { id: 'rate_alert_triggered', label: 'Rate Alert Triggered', description: 'When a rate alert condition is met' }
  ];

  useEffect(() => {
    loadConfig();
    loadStats();
  }, []);
  
  const loadConfig = async () => {
    try {
      const response = await api.get(`/api/admin/integrations`);
      setConfig(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Failed to load integration configuration');
    } finally {
      setLoading(false);
    }
  };
  
  const loadStats = async () => {
    try {
      const response = await api.get(`/api/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/api/admin/integrations`, config);
      toast.success('Integration configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };
  
  const testN8n = async () => {
    setTesting({ ...testing, n8n: true });
    try {
      const response = await api.post(`/api/admin/test-n8n`, {});
      
      if (response.data.success) {
        toast.success('n8n webhook test successful!');
      } else {
        toast.error(`n8n test failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error('n8n test error:', error);
      toast.error(error.response?.data?.detail || 'n8n test failed');
    } finally {
      setTesting({ ...testing, n8n: false });
    }
  };
  
  const triggerTestEvent = async (eventType) => {
    setTriggeringEvent(eventType);
    try {
      const response = await api.post(
        `/api/admin/n8n/trigger-event?event_type=${eventType}`,
        {}
      );
      
      if (response.data.result?.triggered) {
        toast.success(`Event "${eventType}" triggered successfully!`);
      } else {
        toast.warning(`Event not triggered: ${response.data.result?.reason || 'Unknown reason'}`);
      }
    } catch (error) {
      console.error('Event trigger error:', error);
      toast.error(error.response?.data?.detail || 'Failed to trigger event');
    } finally {
      setTriggeringEvent(null);
    }
  };
  
  const toggleEvent = (eventId) => {
    const currentEvents = config.n8n_events || [];
    if (currentEvents.includes(eventId)) {
      setConfig({ ...config, n8n_events: currentEvents.filter(e => e !== eventId) });
    } else {
      setConfig({ ...config, n8n_events: [...currentEvents, eventId] });
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div data-testid="admin-integrations" className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-semibold flex items-center gap-2 mb-2">
          <Settings className="h-6 w-6 text-primary" />
          Admin Integrations
        </h2>
        <p className="text-muted-foreground">
          Configure n8n workflow automation for your app.
        </p>
      </div>
      
      {/* Stats Dashboard */}
      {stats && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Dashboard Overview
            </h3>
            <button
              onClick={loadStats}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              title="Refresh stats"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-background rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Users className="h-4 w-4" />
                Total Users
              </div>
              <p className="text-2xl font-bold">{stats.users?.total || 0}</p>
              <p className="text-xs text-muted-foreground">
                {stats.users?.premium || 0} premium, {stats.users?.pro || 0} pro
              </p>
            </div>
            
            <div className="bg-background rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <TrendingUp className="h-4 w-4" />
                Conversion Rate
              </div>
              <p className="text-2xl font-bold">{stats.users?.conversion_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Paid subscribers</p>
            </div>
            
            <div className="bg-background rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Database className="h-4 w-4" />
                Total Mortgages
              </div>
              <p className="text-2xl font-bold">{stats.mortgages?.total || 0}</p>
              <p className="text-xs text-muted-foreground">
                ${((stats.mortgages?.total_value || 0) / 1000000).toFixed(1)}M total value
              </p>
            </div>
            
            <div className="bg-background rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Zap className="h-4 w-4" />
                Active Alerts
              </div>
              <p className="text-2xl font-bold">{stats.activity?.active_rate_alerts || 0}</p>
              <p className="text-xs text-muted-foreground">Rate alerts monitoring</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-sm">
              <span className={`h-2 w-2 rounded-full ${stats.integrations?.n8n_configured ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
              n8n: {stats.integrations?.n8n_configured ? 'Connected' : 'Not configured'}
            </div>
          </div>
        </div>
      )}
      
      {/* Master Enable Toggle */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Enable Integrations</h3>
            <p className="text-sm text-muted-foreground">Turn on/off all webhook and sync functionality</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
      </div>
      
      {/* n8n Integration */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold">n8n Workflow Automation</h3>
              <p className="text-sm text-muted-foreground">Trigger automated workflows for app events</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Webhook URL</label>
            <input
              type="url"
              value={config.n8n_webhook_url}
              onChange={(e) => setConfig({ ...config, n8n_webhook_url: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="https://your-n8n-instance.com/webhook/..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              Create a webhook trigger in n8n and paste the URL here
            </p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={testN8n}
              disabled={!config.n8n_webhook_url || testing.n8n}
              data-testid="test-n8n-btn"
              className="flex items-center gap-2 px-4 py-2 bg-background border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {testing.n8n ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <TestTube className="h-4 w-4" />
              )}
              Test Connection
            </button>
          </div>
          
          {/* Event Selection */}
          <div className="bg-background rounded-lg p-4">
            <h4 className="text-sm font-semibold mb-3">Enabled Events:</h4>
            <div className="space-y-2">
              {availableEvents.map(event => (
                <label key={event.id} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(config.n8n_events || []).includes(event.id)}
                    onChange={() => toggleEvent(event.id)}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium">{event.label}</span>
                    <span className="text-xs text-muted-foreground ml-2">- {event.description}</span>
                  </div>
                  <button
                    onClick={() => triggerTestEvent(event.id)}
                    disabled={!config.n8n_webhook_url || triggeringEvent === event.id}
                    className="px-2 py-1 text-xs bg-muted hover:bg-muted/80 rounded disabled:opacity-50 transition-colors"
                    title="Send test event"
                  >
                    {triggeringEvent === event.id ? (
                      <Loader className="h-3 w-3 animate-spin" />
                    ) : (
                      <Send className="h-3 w-3" />
                    )}
                  </button>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Webhook Health */}
      <WebhookHealth />
      
      {/* Digest Run History */}
      <DigestRunHistory />
      
      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <button
          onClick={loadConfig}
          className="px-4 py-2 text-sm bg-background border border-border rounded-lg hover:bg-muted transition-colors"
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-integrations-btn"
          className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
      
      {/* Help Section */}
      <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
        <h4 className="text-sm font-semibold mb-2">Need Help?</h4>
        <p className="text-sm text-muted-foreground">
          Check out the integration guides:
        </p>
        <div className="flex gap-4 mt-3 text-sm">
          <a
            href="https://docs.n8n.io/integrations/builtin/core-nodes/n8n-nodes-base.webhook/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            n8n Webhook Docs
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminIntegrations;
